import { useEffect, useState } from 'react';

type Slot = 0 | 1;

interface Slots {
  docs: [string | null, string | null];
  visible: Slot;
}

/**
 * A srcDoc iframe that never goes blank between documents.
 *
 * Changing an iframe's `srcDoc` navigates it: the old document is torn down,
 * the frame paints white, and the new document paints once it has parsed and
 * its stylesheets have loaded. Fine for a document that changes once. A door
 * build, though, repaints its streaming preview every couple of seconds for
 * the whole five-to-nine-minute write phase, and each repaint was one of those
 * white flashes — which every author reads as "it's broken".
 *
 * So: two frames. The next document loads in the hidden one, and it becomes
 * the visible one only after its `load` event, by which point it has painted.
 * A frame's document is only ever changed while that frame is hidden, so the
 * one on screen never reloads in view.
 */
export function BufferedFrame({
  doc,
  title,
  className,
  sandbox,
}: {
  doc: string;
  title: string;
  className?: string;
  sandbox?: string;
}) {
  const [slots, setSlots] = useState<Slots>({ docs: [doc, null], visible: 0 });

  useEffect(() => {
    setSlots((s) => {
      const hidden: Slot = s.visible === 0 ? 1 : 0;
      // Already on screen, or already loading in the wings.
      if (s.docs[s.visible] === doc || s.docs[hidden] === doc) return s;
      const docs: Slots['docs'] = [s.docs[0], s.docs[1]];
      docs[hidden] = doc;
      return { docs, visible: s.visible };
    });
  }, [doc]);

  // `load` fires for the document a frame has just finished painting. Only the
  // hidden frame ever has a newer document than the visible one, so a load
  // there is the moment to swap.
  const reveal = (slot: Slot) => () =>
    setSlots((s) => (slot === s.visible || s.docs[slot] === null ? s : { ...s, visible: slot }));

  return (
    <div className="relative w-full h-full">
      {([0, 1] as const).map((slot) =>
        slots.docs[slot] === null ? null : (
          <iframe
            key={slot}
            srcDoc={slots.docs[slot] ?? undefined}
            title={slot === slots.visible ? title : `${title} (loading)`}
            aria-hidden={slot !== slots.visible}
            onLoad={reveal(slot)}
            data-slot={slot}
            className={`absolute inset-0 w-full h-full border-0 ${className ?? ''}`}
            style={{ opacity: slot === slots.visible ? 1 : 0 }}
            sandbox={sandbox}
          />
        ),
      )}
    </div>
  );
}
